/* -------------------------------------------------------------------------- */
/*  Budget-import framework — orchestrator                                      */
/* -------------------------------------------------------------------------- *
 *  The one pipeline every budget import runs: read the loaded estimate (plus   *
 *  whatever `requires` asks for), assemble a BudgetImportContext, and dispatch *
 *  to the family renderer. Tenants never repeat the fetch/guard boilerplate.   */
/* -------------------------------------------------------------------------- */

import { ValidationError } from "../../../errors";
import { getEstimateReportData, getGCGRReportData, getProjectReportData } from "../../../excel/workbook";
import { fetchLoadedProject } from "../../../excel/workbook/state";
import { getProcessedEstimate } from "../../../excel/datasources/estimate";
import { fetchFloorAreasWithGroups } from "../../../excel/reports/areaSheet";
import { fetchGCGRStaffWithDetails, fetchGCGRLineItemsWithGroups } from "../../../excel/reports/gcgr";
import { formatDateForSage } from "../categories";
import { BudgetFormat, BudgetImportConfig, BudgetImportContext, BudgetRow } from "./types";
import { renderSage } from "./renderers/sage";
import { renderVista } from "./renderers/vista";

type FamilyRenderer = (ctx: BudgetImportContext, config: BudgetImportConfig) => string[];

const RENDERERS: Record<BudgetFormat, FamilyRenderer> = {
  sage: renderSage,
  vista: renderVista,
};

const extensionOf = (config: BudgetImportConfig): string =>
  config.format === "sage" ? config.sage.ext : config.vista.ext;

/** The loaded GCGR's tables, or undefined when the workbook holds no GCGR. */
const fetchLoadedGcgrTables = async (): Promise<BudgetImportContext["gcgr"]> => {
  // The GCGR names its own project: datasources never read each other's metadata rows.
  const gcgrReport = (await getGCGRReportData())[0];
  if (!gcgrReport?.gcgr_id) return undefined;
  const projectId = gcgrReport.project_id as string | undefined;
  // Sequential: each of these reads the GCGR metadata row before calling the API.
  const staff = await fetchGCGRStaffWithDetails(projectId);
  const lineItems = await fetchGCGRLineItemsWithGroups(projectId);
  return { staff, lineItems };
};

/** Run a tenant's budget import: fetch, build context, render, and return `[lines, extension]`. */
export async function runBudgetImport(config: BudgetImportConfig): Promise<[string[], string]> {
  const estimateReport = ((await getEstimateReportData())[0] ?? {}) as Record<string, any>;
  const estimateId = estimateReport.estimate_id as string | undefined;
  if (!estimateId) throw new ValidationError("Load an estimate before exporting.");
  const projectId = estimateReport.project_id as string | undefined;
  const requires = config.requires ?? {};

  // Workbook reads go one at a time, since Office batches must not overlap. The API fetches
  // below never touch the workbook, so they can run together.
  const projectReport = requires.projectReport
    ? (((await getProjectReportData())[0] ?? {}) as Record<string, any>)
    : {};
  const project = requires.project ? await fetchLoadedProject() : undefined;
  if (requires.project && !project) throw new ValidationError("Load a project before exporting.");
  const gcgr = requires.gcgr ? await fetchLoadedGcgrTables() : undefined;

  const [processed, floorAreaRows] = await Promise.all([
    getProcessedEstimate(estimateId),
    requires.floorAreas ? fetchFloorAreasWithGroups(estimateId) : Promise.resolve([]),
  ]);

  const ctx: BudgetImportContext = {
    estimateId,
    projectId,
    rows: (processed?.baseBidItems ?? []) as BudgetRow[],
    assemblyChildren: requires.assemblyChildren ? ((processed?.childItems ?? []) as BudgetRow[]) : [],
    estimateReport,
    projectReport,
    project,
    totalArea: floorAreaRows.reduce((sum: number, row: any) => sum + (row.total_area ?? 0), 0),
    gcgr,
    dateNum: formatDateForSage(),
  };

  return [RENDERERS[config.format](ctx, config), extensionOf(config)];
}
